import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './pm-viewer.style.js';
import { PMTemplate } from '../../../types/pm.types.js';
import { getAssetsByCompany, getUsersByCompany, updatePreventiveMaintenance } from '@cmms/api';
import { db } from '../../services/auth.service.js';

@customElement('pm-viewer')
export class PMViewer extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) pmTemplate: PMTemplate | null = null;
  @property({ type: String }) companyId: string = '';

  @state() private activeTab = 'info';
  @state() private assets: any[] = [];
  @state() private users: any[] = [];
  @state() private isEditing = false;
  @state() private isLoading = false;
  @state() private errorMessage = '';
  @state() private originalPMTemplate: any = null;

  connectedCallback() {
    super.connectedCallback();
    // Don't load immediately - wait for properties to be set
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('companyId') && this.companyId) {
      this.loadAssetsAndUsers();
    }
    if (changedProperties.has('pmTemplate') && this.pmTemplate) {
      // PM template received, data will be loaded if companyId is available
    }
    
    // Load data when both companyId and pmTemplate are available
    if (this.companyId && this.pmTemplate && (changedProperties.has('companyId') || changedProperties.has('pmTemplate'))) {
      this.loadAssetsAndUsers();
    }
  }

  private async loadAssetsAndUsers() {
    if (!this.companyId) {
      return;
    }
    
    try {
      const [assetsResult, usersResult] = await Promise.all([
        getAssetsByCompany(db, this.companyId),
        getUsersByCompany(db, this.companyId)
      ]);
      
      this.assets = this.asArray(assetsResult);
      this.users = this.asArray(usersResult);
      
      // Force re-render to update select elements
      this.requestUpdate();
      
      // Update select elements after render
      this.updateComplete.then(() => {
        this.updateSelectElements();
      });
    } catch (error) {
      console.error('Error loading assets and users:', error);
    }
  }

  private asArray<T>(result: any): T[] {
    if (result?.data) return Array.isArray(result.data) ? result.data : [result.data];
    if (Array.isArray(result)) return result;
    return [];
  }

  private updateSelectElements() {
    // Update asset select
    const assetSelect = this.shadowRoot?.querySelector('sl-select[data-field="asset"]') as any;
    if (assetSelect && this.pmTemplate?.model?.asset_id) {
      assetSelect.value = this.pmTemplate.model.asset_id;
    }
    
    // Update assigned to select
    const assignedSelect = this.shadowRoot?.querySelector('sl-select[data-field="assigned"]') as any;
    if (assignedSelect && this.pmTemplate?.model?.default_assigned_to) {
      assignedSelect.value = this.pmTemplate.model.default_assigned_to;
    }
  }

  render() {
    if (!this.pmTemplate) return html``;

    const pmName = this.pmTemplate.model?.name || 'Unnamed PM';
    const pmDescription = this.pmTemplate.model?.description || 'No description available';
    const assetId = this.pmTemplate.model?.asset_id || '';
    const frequency = this.formatFrequency(this.pmTemplate);
    const nextDue = this.formatDate(this.pmTemplate.model?.next_due_at);
    const graceDays = this.pmTemplate.model?.grace_days || 0;
    const priority = this.getPriorityName(this.pmTemplate.model?.default_priority_cat || 2);
    const assignedTo = this.pmTemplate.model?.default_assigned_to || 'Not assigned';
    const isActive = this.pmTemplate.model?.active !== false;
    const createdDate = this.formatDate(this.pmTemplate.model?.created_at);

    return html`
      <sl-dialog
        label="PM Viewer"
        .open=${this.open}
        @sl-after-hide=${this.onDialogAfterHide}
      >
        <div class="pm-header">
          <h2>${pmName}</h2>
          <div class="pm-status-badge">
            <sl-badge variant=${isActive ? 'success' : 'neutral'}>
              ${isActive ? 'Active' : 'Inactive'}
            </sl-badge>
          </div>
        </div>

        <div class="tab-navigation">
          <button class="tab-button active" @click=${() => this.activeTab = 'info'}>
            <sl-icon name="info-circle"></sl-icon> Info
          </button>
          <button class="tab-button" @click=${() => this.activeTab = 'schedule'}>
            <sl-icon name="calendar"></sl-icon> Schedule
          </button>
          <button class="tab-button" @click=${() => this.activeTab = 'checklist'}>
            <sl-icon name="list"></sl-icon> Checklist
          </button>
          <button class="tab-button" @click=${() => this.activeTab = 'parts'}>
            <sl-icon name="box"></sl-icon> Parts
          </button>
        </div>

        ${this.activeTab === 'info' ? html`
          ${this.errorMessage ? html`
            <div class="error-message">
              <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
              ${this.errorMessage}
            </div>
          ` : ''}

          <div class="pm-fields">
            <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
              <div class="field-label">Name:</div>
              <div class="field-value">
                <sl-input
                  .value=${this.pmTemplate?.model?.name || ''}
                  @sl-input=${(e: any) => this.pmTemplate = { ...this.pmTemplate!, model: { ...this.pmTemplate!.model, name: e.target.value } }}
                  @click=${(e: Event) => e.stopPropagation()}
                  @sl-hide=${(e: Event) => e.stopPropagation()}
                  @sl-show=${(e: Event) => e.stopPropagation()}
                  @sl-after-show=${(e: Event) => e.stopPropagation()}
                  @sl-after-hide=${(e: Event) => e.stopPropagation()}
                  size="large"
                  ?disabled=${!this.isEditing}
                ></sl-input>
              </div>
            </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Description:</div>
            <div class="field-value">
              <sl-textarea
                .value=${this.pmTemplate?.model?.description || ''}
                @sl-input=${(e: any) => this.pmTemplate = { ...this.pmTemplate!, model: { ...this.pmTemplate!.model, description: e.target.value } }}
                @click=${(e: Event) => e.stopPropagation()}
                rows="3"
                resize="vertical"
                ?disabled=${!this.isEditing}
              ></sl-textarea>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Asset:</div>
            <div class="field-value">
              <sl-select
                data-field="asset"
                .value=${this.pmTemplate?.model?.asset_id || ''}
                @sl-change=${(e: any) => this.pmTemplate = { ...this.pmTemplate!, model: { ...this.pmTemplate!.model, asset_id: e.target.value } }}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-after-show=${(e: Event) => e.stopPropagation()}
                @sl-after-hide=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
              >
                <sl-option value="">No asset selected</sl-option>
                ${this.assets.length > 0 ? this.assets.map(asset => html`
                  <sl-option value=${asset.id || asset.$id}>${asset.model?.name || asset.name || asset.id || asset.$id}</sl-option>
                `) : html`<sl-option value="" disabled>Loading assets...</sl-option>`}
              </sl-select>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Priority:</div>
            <div class="field-value">
              <sl-select
                .value=${String(this.pmTemplate?.model?.default_priority_cat || 2)}
                @sl-change=${(e: any) => this.pmTemplate = { ...this.pmTemplate!, model: { ...this.pmTemplate!.model, default_priority_cat: parseInt(e.target.value) } }}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-after-show=${(e: Event) => e.stopPropagation()}
                @sl-after-hide=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
              >
                <sl-option value="1">Low</sl-option>
                <sl-option value="2">Medium</sl-option>
                <sl-option value="3">High</sl-option>
                <sl-option value="4">Critical</sl-option>
              </sl-select>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Assigned To:</div>
            <div class="field-value">
              <sl-select
                data-field="assigned"
                .value=${this.pmTemplate?.model?.default_assigned_to || ''}
                @sl-change=${(e: any) => this.pmTemplate = { ...this.pmTemplate!, model: { ...this.pmTemplate!.model, default_assigned_to: e.target.value } }}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-after-show=${(e: Event) => e.stopPropagation()}
                @sl-after-hide=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
              >
                <sl-option value="">Not assigned</sl-option>
                ${this.users.length > 0 ? this.users.map(user => html`
                  <sl-option value=${user.id || user.$id}>${user.model?.name || user.name || user.email || user.id || user.$id}</sl-option>
                `) : html`<sl-option value="" disabled>Loading users...</sl-option>`}
              </sl-select>
            </div>
          </div>

          <div class="field-row">
            <div class="field-label">Created:</div>
            <div class="field-value">${createdDate}</div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Status:</div>
            <div class="field-value">
              <sl-checkbox
                ?checked=${this.pmTemplate?.model?.active !== false}
                @sl-change=${(e: any) => this.pmTemplate = { ...this.pmTemplate!, model: { ...this.pmTemplate!.model, active: e.target.checked } }}
                @click=${(e: Event) => e.stopPropagation()}
                ?disabled=${!this.isEditing}
              >
                Active
              </sl-checkbox>
            </div>
          </div>
          </div>
        ` : ''}

        ${this.activeTab === 'schedule' ? html`
          <div class="info-description">
            Schedule - View frequency and scheduling information.
          </div>

          <div class="field-row">
            <div class="field-label">Frequency:</div>
            <div class="field-value">${frequency}</div>
          </div>

          <div class="field-row">
            <div class="field-label">Next Due:</div>
            <div class="field-value">${nextDue}</div>
          </div>

          <div class="field-row">
            <div class="field-label">Grace Period:</div>
            <div class="field-value">${graceDays} days</div>
          </div>
        ` : ''}

        ${this.activeTab === 'checklist' ? html`
          <div class="info-description">
            Checklist - View maintenance steps and procedures.
          </div>

          ${this.pmTemplate.model?.checklist?.length ? html`
            <div class="checklist">
              ${this.pmTemplate.model.checklist.map((item: any, index: number) => html`
                <div class="checklist-item">
                  <div class="checklist-number">${index + 1}</div>
                  <div class="checklist-content">
                    <div class="checklist-text">${item.text}</div>
                    ${item.required ? html`
                      <sl-badge variant="warning" size="small">Required</sl-badge>
                    ` : html`
                      <sl-badge variant="neutral" size="small">Optional</sl-badge>
                    `}
                  </div>
                </div>
              `)}
            </div>
          ` : html`
            <div class="empty-checklist">
              <sl-icon name="list" size="large"></sl-icon>
              <p>No checklist items defined for this PM template.</p>
            </div>
          `}
        ` : ''}

        ${this.activeTab === 'parts' ? html`
          <div class="info-description">
            Parts - View required parts and quantities.
          </div>

          ${this.pmTemplate.model?.parts?.length ? html`
            <div class="parts-list">
              ${this.pmTemplate.model.parts.map((part: any, index: number) => html`
                <div class="part-item">
                  <div class="part-info">
                    <div class="part-name">${part.part_id || `Part ${index + 1}`}</div>
                    <div class="part-quantity">Qty: ${part.quantity || 1}</div>
                  </div>
                </div>
              `)}
            </div>
          ` : html`
            <div class="empty-parts">
              <sl-icon name="box" size="large"></sl-icon>
              <p>No parts defined for this PM template.</p>
            </div>
          `}
        ` : ''}

        <div class="dialog-footer">
          ${this.isEditing ? html`
            <sl-button
              variant="neutral"
              size="large"
              @click=${this.handleCancel}
              ?disabled=${this.isLoading}
            >
              Cancel
            </sl-button>
            <sl-button
              variant="primary"
              size="large"
              @click=${this.handleSave}
              ?loading=${this.isLoading}
              ?disabled=${this.isLoading}
            >
              ${this.isLoading ? 'Saving...' : 'Save'}
            </sl-button>
          ` : html`
            <sl-button
              variant="neutral"
              size="large"
              @click=${this.handleEdit}
            >
              Edit
            </sl-button>
            <sl-button
              variant="primary"
              size="large"
              @click=${this.closeModal}
            >
              Done
            </sl-button>
          `}
        </div>
      </sl-dialog>
    `;
  }

  private formatFrequency(pmTemplate: PMTemplate): string {
    if (!pmTemplate.model) return 'Not set';
    const { freq_value, freq_unit } = pmTemplate.model;
    if (!freq_value || !freq_unit) return 'Not set';
    const unit = freq_unit === 'days' ? 'day' : freq_unit === 'weeks' ? 'week' : 'month';
    const plural = freq_value > 1 ? 's' : '';
    return `Every ${freq_value} ${unit}${plural}`;
  }

  private formatDate(timestamp?: number): string {
    if (!timestamp) return '—';
    try {
      return new Date(timestamp).toLocaleDateString();
    } catch { 
      return '—'; 
    }
  }

  private getPriorityName(priorityCat: number): string {
    const priorityMap: Record<number, string> = {
      1: 'Low',
      2: 'Medium',
      3: 'High',
      4: 'Critical'
    };
    return priorityMap[priorityCat] || 'Medium';
  }

  private getAssetName(assetId: string): string {
    if (!assetId) return 'Not assigned';
    const asset = this.assets.find(a => a.id === assetId || a.$id === assetId || a.asset_id === assetId);
    if (!asset) return `Asset ${assetId}`;
    
    // Based on the console log, the name is in asset.model.name
    return asset.model?.name ;
  }

  private getUserName(userId: string): string {
    if (!userId) return 'Not assigned';
    const user = this.users.find(u => u.id === userId || u.$id === userId || u.user_id === userId || u.uid === userId);
    return user?.model?.name || user?.model?.display_name || user?.name || user?.display_name || user?.email || `User ${userId}`;
  }

  private onDialogAfterHide = () => {
    this.dispatchEvent(new CustomEvent('modal-closed'));
  };

  private closeModal = () => {
    this.open = false;
  };

  private handleEdit = () => {
    this.originalPMTemplate = JSON.parse(JSON.stringify(this.pmTemplate));
    this.isEditing = true;
    this.errorMessage = '';
  };

  private handleCancel = () => {
    this.pmTemplate = this.originalPMTemplate;
    this.isEditing = false;
    this.originalPMTemplate = null;
    this.errorMessage = '';
  };

  private async handleSave() {
    if (!this.pmTemplate) return;

    try {
      this.isLoading = true;
      this.errorMessage = '';

      const updateData = {
        ...this.pmTemplate.model,
        updated_at: Date.now()
      };

      const result = await updatePreventiveMaintenance(db, this.pmTemplate.id, updateData);
      
      if (!result.error) {
        // Update successful
        this.pmTemplate = { ...this.pmTemplate, model: updateData };
        this.isEditing = false;
        this.originalPMTemplate = null;
        
        // Dispatch event to notify parent component
        this.dispatchEvent(new CustomEvent('pm-updated', {
          detail: result.data,
          bubbles: true,
          composed: true
        }));
      } else {
        this.errorMessage = result.error || 'Failed to update PM template';
      }
    } catch (error: any) {
      console.error('Error updating PM template:', error);
      this.errorMessage = error.message || 'Failed to update PM template';
    } finally {
      this.isLoading = false;
    }
  };

  static styles = styles;
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-viewer': PMViewer;
  }
}
